import { useMemo, useState } from "react"
import { isFakeAttribution } from "../demo"
import { Panel } from "../Panel"
import type { IntelItem } from "../dashboard-types"
import { useRegion } from "../store"

const TAG_COLORS: Record<string, { bg: string; text: string }> = {
  ALERT: { bg: "bg-red-500/15", text: "text-red-400" },
  "SUPPLY CHAIN": { bg: "bg-sky-500/15", text: "text-sky-400" },
  MILITARY: { bg: "bg-orange-500/15", text: "text-orange-400" },
  MARKET: { bg: "bg-emerald-500/15", text: "text-emerald-400" },
  CYBER: { bg: "bg-purple-500/15", text: "text-purple-400" },
  WEATHER: { bg: "bg-sky-500/15", text: "text-sky-400" },
}

function tagStyle(tag: string): { bg: string; text: string } {
  return TAG_COLORS[tag.toUpperCase()] ?? { bg: "bg-zinc-800", text: "text-zinc-400" }
}

function relTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const m = Math.floor(diff / 60_000)
  if (m < 1) return "just now"
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24)
  return `${d}d ago`
}

export function LiveIntelligence({ items, demo }: { items: IntelItem[]; demo?: boolean }) {
  const openRegion = useRegion((s) => s.openRegion)

  const categories = useMemo(() => {
    const unique = Array.from(new Set(items.map((i) => i.category)))
    return ["all", ...unique]
  }, [items])

  const [activeTab, setActiveTab] = useState<string>("all")

  const filtered = activeTab === "all" ? items : items.filter((i) => i.category === activeTab)

  return (
    <Panel
      title="Live Intelligence"
      demo={demo}
      count={items.length}
      helpText={
        demo
          ? "Sample intelligence items for demo purposes — not real news coverage"
          : "Real-time intelligence feeds from global logistics and geopolitical sources"
      }
    >
      {/* Tabs */}
      <div className="px-3 pt-2 pb-1 flex flex-wrap gap-1 border-b border-[color:var(--border)]">
        {categories.map((cat) => (
          <button
            key={cat}
            type="button"
            aria-pressed={activeTab === cat}
            onClick={() => setActiveTab(cat)}
            className="btn-pill text-[9px]"
          >
            {cat.replace("_", " ")}
          </button>
        ))}
      </div>

      {/* Items */}
      <div>
        {filtered.map((it) => (
          <div key={it.id} className="px-3 py-2 border-b border-[color:var(--border)] last:border-b-0">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] font-bold text-[color:var(--text-muted)] uppercase tracking-wider font-mono">
                {/* Never imply coverage by a real outlet for synthetic items. */}
                {it.demo || demo || isFakeAttribution(it.source_name)
                  ? "SIMULATED"
                  : it.source_name}
              </span>
              <span className="flex items-center gap-1.5 text-[9px] text-[color:var(--text-dim)] whitespace-nowrap">
                {relTime(it.published_at)}
                {it.rate_per_hour > 0 && (
                  <span className="text-orange-400">(+{it.rate_per_hour}/HR)</span>
                )}
              </span>
            </div>
            <div className="mt-1 flex items-center gap-1 flex-wrap">
              {it.tags.map((tag) => {
                const ts = tagStyle(tag)
                return (
                  <span key={tag} className={`text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider ${ts.bg} ${ts.text}`}>
                    {tag}
                  </span>
                )
              })}
              <button
                type="button"
                onClick={() => openRegion(it.region)}
                className="text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-200 transition-colors cursor-pointer focus:outline-none focus:ring-1 focus:ring-[color:var(--accent)]"
              >
                {it.region}
              </button>
            </div>
            <div className="mt-1 text-sm text-[color:var(--text)] leading-snug">{it.title}</div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="px-3 py-6 text-center text-[11px] text-[color:var(--text-dim)] uppercase tracking-wider">
            no intelligence items
          </div>
        )}
      </div>
    </Panel>
  )
}
